import { useSessionMessageApi } from '../../../../utils/actionableNotifications';
import React, { useState, useEffect } from "react";
import { Alert, Modal, Input, Tag, Spin } from 'antd';
import { injectUnavailablePartsTemplate, useQv4Theme } from "../../helpers";
import { useActionSnippet } from "../../../messaging/useActionSnippet";
import { useQueryLifecycle } from "../../viewmodels/modals-misc/useQueryLifecycle";
import { BusinessSendRecovery } from "./BusinessSendRecovery";
import { PlaceholderWarning } from '../../../messaging/PlaceholderWarning';

// Standalone "let the customer know some part(s) couldn't be sourced" notice —
// usable whenever a task has parts nobody quoted, independent of the task's
// overall status. Same backend endpoint regardless of where it's opened from
// (mark-as-lost, the task drawer button, or the post-quote-share nudge).
export function UnavailablePartsModal({ open, queryPk, queryId, customerName, customerPhone, communicationChannel, unavailableParts, userId, onClose, onSuccess, placeholderContext = {} }) {
  const message = useSessionMessageApi();
  const [messageText, setMessageText] = useState("");
  const { snippet, loading: templateLoading, error: bindingError } = useActionSnippet(open, "parts_unavailable");
  const [sending, setSending] = useState(false);
  const { t } = useQv4Theme();
  const { notifyUnavailable } = useQueryLifecycle(queryPk, userId, communicationChannel);

  useEffect(() => {
    if (!open) return;
    if (!snippet) { setMessageText(""); return; }
        setMessageText(injectUnavailablePartsTemplate(snippet.body, {
          ...placeholderContext, customerPhone,
          customerName,
          queryId,
          parts: unavailableParts,
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, snippet]);

  const handleSend = async () => {
    if (templateLoading || !snippet || bindingError || sending) return;
    if (!messageText.trim()) { message.warning("Message can't be empty"); return; }
    if (communicationChannel?.key === "wati" && !customerPhone) { message.error("No phone number on file for this customer"); return; }
    setSending(true);
    try {
      const partIds = (unavailableParts || []).map(p => p.id ?? p.query_part_id).filter(Boolean);
      const res = await notifyUnavailable(messageText.trim(), partIds);
      if (!res?.success) throw new Error(res?.message);
      message.success(res.messaging_operation ? "Notice accepted by the provider; delivery is tracked in chat." : res.message || (communicationChannel?.key === "wati" ? "Customer notified" : "Manual notification recorded"));
      onSuccess?.();
      onClose();
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`Notify Customer — Unavailable Parts${queryId ? ` — #${queryId}` : ""}`}
      onCancel={onClose}
      onOk={handleSend}
      confirmLoading={sending}
      okButtonProps={{ disabled: templateLoading || !snippet || !!bindingError || !messageText.trim() }}
      okText={communicationChannel?.key && communicationChannel.key !== "wati" ? "Record as completed" : "Send to Customer"}
      cancelText="Cancel"
      destroyOnHidden
      width={520}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
        <BusinessSendRecovery actor={userId} query={queryPk} action="notify-unavailable-parts" onRecovered={() => { onSuccess?.(); onClose(); }} />
        {bindingError && <Alert type="error" message={bindingError} />}
        <PlaceholderWarning text={messageText} />
        {communicationChannel?.key && communicationChannel.key !== "wati" && <Alert showIcon type="warning" message={`This query uses ${communicationChannel.label}. Uzdpart360 cannot send this automatically. Continuing records that you completed it manually.`} />}
        <div style={{ fontSize: 13, color: t.textSub }}>
          {customerName || "This customer"} will be told the part(s) below couldn't be sourced.
          No approval is needed — this is a courtesy notice, not a quote.
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(unavailableParts || []).map((p, i) => (
            <Tag key={p.id ?? p.query_part_id ?? i} color="orange">{p.part_name || `Part ${i + 1}`}</Tag>
          ))}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: t.textSub }}>
            Message <span style={{ color: "#ff4d4f" }}>*</span>
          </div>
          {templateLoading ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}><Spin /></div>
          ) : (
            <Input.TextArea
              rows={9}
              value={messageText}
              onChange={e => setMessageText(e.target.value)}
              placeholder="Message to send"
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
